import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, UserClaims } from '@okta/okta-auth-js';
import { Observable, ReplaySubject, catchError, debounceTime, filter, map, of, switchMap, tap } from 'rxjs';

/**
 * Service responsible for authenticating a user with Okta.
 * This service is exposed publicly for simplicity but should
 * only be injected into UserService. The rest of the application
 * should depend on UserService.isAuthenticated$ to determine
 * if a user is logged in or not.
 *
 * Supports silent login: if the user has an active Okta session
 * (e.g., logged in via another app), this service will detect it
 * and authenticate without user interaction.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
    private authStateService = inject(OktaAuthStateService);
    private oktaAuth = inject<OktaAuth>(OKTA_AUTH);
    private location = inject(Location);

    private _isAuthenticated$ = new ReplaySubject<boolean>(1);
    private _claims$: ReplaySubject<UserClaims> = new ReplaySubject(1);

    constructor() {
        // OktaAuthStateService detects auth state immediately upon construction.
        // Handles auto-login from localStorage tokens.
        // This is a global service so this will happen when the app starts.
        this.authStateService.authState$
            .pipe(
                // Debounce the observable by 250 milliseconds to guard against repetitive and fast updates between true/false.
                // Rapid toggling can occur while a sign-in/out process is in an intermediate state during token caching or cleanup.
                debounceTime(250),
                tap((state) => {
                    console.debug(`[Services][AuthService] Updated auth state to ${state.isAuthenticated}`);
                    this._isAuthenticated$.next(state.isAuthenticated);
                }),
                map((state) => state.isAuthenticated),
                filter((isAuthenticated) => isAuthenticated === true),
                switchMap(() => this.oktaAuth.getUser()),
                catchError((error) => {
                    console.error('[Services][AuthService] Error getting user claims:', error);
                    return of(null);
                }),
                filter((claims) => claims !== null),
                tap((claims) => this._claims$.next(claims)),
            )
            .subscribe({
                error: (error) => {
                    console.error('[Services][AuthService] Error in auth state subscription:', error);
                    this._isAuthenticated$.next(false);
                },
            });

        // Silent login: if no tokens exist locally, check if the user has an
        // active Okta session (e.g., logged in from another app). Uses a hidden
        // iframe with prompt=none per the OIDC spec. If a session exists, tokens
        // are obtained silently and stored — authState$ will emit the update.
        // If no session exists, the user simply sees the public/unauthed page.
        this.attemptSilentLogin();
    }

    /**
     * Attempts to silently obtain tokens using an existing Okta session.
     * This enables cross-app SSO: if the user is logged into Okta via any app,
     * they'll be automatically authenticated here without clicking "login."
     *
     * Uses `token.getWithoutPrompt()` which opens a hidden iframe to Okta's
     * /authorize endpoint with `prompt=none`. This is a standard OIDC mechanism.
     */
    private async attemptSilentLogin(): Promise<void> {
        try {
            // Skip if the app is currently handling an OAuth callback.
            // The OktaCallbackComponent will handle the token exchange on this route.
            // Running getWithoutPrompt() concurrently would cause a PKCE codeVerifier collision.
            if (window.location.pathname.includes('/login/callback')) {
                console.debug('[Services][AuthService] On callback route, skipping silent login.');
                return;
            }

            // Skip if tokens already exist in storage (user is already authenticated)
            const existingTokens = await this.oktaAuth.tokenManager.getTokens();
            if (existingTokens.accessToken || existingTokens.idToken) {
                console.debug('[Services][AuthService] Tokens exist in storage, skipping silent login.');
                return;
            }

            console.debug('[Services][AuthService] No local tokens. Attempting silent login...');
            const response = await this.oktaAuth.token.getWithoutPrompt();
            this.oktaAuth.tokenManager.setTokens(response.tokens);
            console.debug('[Services][AuthService] Silent login successful.');
        } catch (error: any) {
            // Expected when user has no active Okta session — not an error condition.
            console.debug(
                '[Services][AuthService] Silent login not available:',
                error?.errorCode || error?.message || 'no session',
            );
        }
    }

    /**
     * Do not use. Use UserService.isAuthenticated$ to reduce injected dependencies.
     * @returns observable indicating true/false
     */
    get isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated$.asObservable();
    }

    /**
     * @returns User claims within the Okta JWT.
     */
    get claims$(): Observable<UserClaims> {
        return this._claims$;
    }

    /**
     * @returns the Okta JWT's access token
     */
    get accessToken(): string {
        return this.oktaAuth.getAccessToken();
    }

    /**
     * Redirects a user to the Okta hosted login page for signing in
     * The OktaCallbackComponent setup in app-routing.module.ts handles
     * all the token management on successful authentication.
     *
     * @returns a void Promise
     */
    async signInWithRedirect(): Promise<void> {
        // NOTE: Redirect back to window location path name or current slug route (ex. /education-an-training)
        // and the window location search (ex. ?query=genesys%20cloud), if there was any pre-existing search.
        return this.oktaAuth.signInWithRedirect({
            originalUri: this.location.path(true),
        });
    }

    /**
     * Terminates the session with the GKN Okta app, but not other Okta apps.
     * Removes JWT from local storage
     * @returns boolean for successful sign out
     */
    async signOut(): Promise<boolean> {
        return this.oktaAuth.signOut();
    }
}
